import type { ReactNode } from 'react';
import { useContext, useMemo, useState } from 'react';
import { get, isEmpty } from 'lodash-es';
import { useFormContext, useFormState } from 'react-hook-form';
import { useNamingConventions } from '../../../utils/naming-conventions';
import { isRecord } from '../../../utils/typeGuards';
import Tooltip from '../../../utils/tooltip';
import { useHiddenByConfigHelper } from '../../pages/measuringPoint/configHelper';
import InformationTooltip from '../InformationTooltip';
import { FormFieldContext, OVERRIDES_NAME, useOverrideState, useSharedFormContext } from '../utils';

interface FormFieldContainerFieldOptions {
    name: string;
    settings?: unknown;
}

function getSafeString(obj: unknown, key: string): string | undefined {
    if (!isRecord(obj)) {
        return undefined;
    }

    const value = obj[key];
    return typeof value === 'string' ? value : undefined;
}

interface OverrideCheckboxProps {
    name: string;
    label: string;
    isOverridden: boolean;
}

function OverrideCheckbox({ name, label, isOverridden }: OverrideCheckboxProps): JSX.Element {
    const { register } = useFormContext();
    const context = useContext(FormFieldContext);

    const hover = context?.hover ?? false;
    const setHover = context?.setHover ?? (() => {});

    return (
        <Tooltip visible={hover} content={gettext('Check to override project settings.')}>
            <input
                type="checkbox"
                id={`${OVERRIDES_NAME}-id_${name}`}
                {...register(`${OVERRIDES_NAME}.${name}`)}
                onMouseOver={() => {
                    setHover(true);
                }}
                onMouseLeave={() => {
                    setHover(false);
                }}
                checked={isOverridden}
                aria-label={interpolate(gettext('OVERRIDE_CHECKBOX_LABEL'), { field: label }, true)}
            />
        </Tooltip>
    );
}

interface TopRightItemsProps {
    name: string;
    label: string;
    showOverrideCheckbox: boolean;
    render: (items: ReactNode[]) => ReactNode;
}

function TopRightItems({
    name,
    label,
    showOverrideCheckbox,
    render,
}: TopRightItemsProps): JSX.Element | null {
    const { formController } = useSharedFormContext();

    const { overridable, overridden: isOverridden } = useOverrideState(name);

    const items = useMemo(() => {
        const possibleItems: ReactNode[] = [];

        if (name && formController.information?.[name]) {
            possibleItems.push(
                <InformationTooltip key={1} information={formController.information[name]} />
            );
        }

        if (overridable && showOverrideCheckbox) {
            possibleItems.push(<OverrideCheckbox key={2} {...{ name, label, isOverridden }} />);
        }

        return possibleItems;
    }, [name, showOverrideCheckbox, formController.information, overridable, isOverridden, label]);

    if (isEmpty(items)) {
        return null;
    }

    return <>{render(items)}</>;
}

interface BaseFormFieldContainerProps {
    name?: string | null;
    Element?: keyof JSX.IntrinsicElements;
    inline?: boolean;
    className?: string;
    label?: string | null;
    showLabel?: boolean;
    showOverrideCheckbox?: boolean;
    error?: string | null;
    helpText?: string;
    children: ReactNode;
}

export function BaseFormFieldContainer({
    name = null,
    Element = 'div',
    inline = false,
    className = '',
    label = null,
    showLabel = true,
    showOverrideCheckbox = false,
    error,
    helpText,
    children,
}: BaseFormFieldContainerProps): JSX.Element {
    const fieldId = `id_${name}`;
    const labelId = `form-field-label-${name}`;

    return (
        <Element
            className={`text-form-text relative list-none align-top ${className} ${
                inline ? 'inline-block w-[250px] shrink-0' : ''
            }`}
        >
            {showLabel ? (
                // When we need to show a label, we let the top right items
                // float right next to it.
                <>
                    <div>
                        <TopRightItems
                            name={name ?? ''}
                            label={label ?? ''}
                            showOverrideCheckbox={showOverrideCheckbox}
                            render={(items) => (
                                <span className="float-right ml-2 flex gap-x-1">{items}</span>
                            )}
                        />
                        <label
                            id={labelId}
                            htmlFor={fieldId}
                            className="text-[11px]"
                            onClick={() => {
                                if (fieldId) {
                                    document.getElementById(fieldId)?.focus();
                                }
                            }}
                        >
                            {label}
                        </label>
                    </div>
                    {children}
                </>
            ) : (
                // If we don't need to show a label, we shown the top right
                // items in a div next to it.
                <div className="flex items-start gap-x-2">
                    <div>{children}</div>
                    <TopRightItems
                        name={name ?? ''}
                        label={label ?? ''}
                        showOverrideCheckbox={showOverrideCheckbox}
                        render={(items) => <div className="flex gap-x-1">{items}</div>}
                    />
                </div>
            )}
            <span className="text-[11px]">
                {error && <div className="text-form-warning">{error}</div>}
                {helpText}
            </span>
        </Element>
    );
}

interface FormFieldContainerProps {
    fieldOptions: FormFieldContainerFieldOptions;
    children: ReactNode;
    showOverrideCheckbox?: boolean;
    showError?: boolean;
    showLabel?: boolean;
    inline?: boolean;
    className?: string;
}

export default function FormFieldContainer({
    fieldOptions,
    children,
    showOverrideCheckbox = true,
    showError = true,
    showLabel = true,
    ...props
}: FormFieldContainerProps): JSX.Element | null {
    const { name, settings } = fieldOptions;
    const hide = useHiddenByConfigHelper(name);
    const label = useNamingConventions(getSafeString(settings, 'label') ?? '');

    const { errors } = useFormState();

    const [hover, setHover] = useState(false);

    if (hide) {
        return null;
    }

    const rawError = get(errors, `${name}.message`);

    return (
        <FormFieldContext.Provider
            value={{
                hover,
                setHover,
            }}
        >
            <BaseFormFieldContainer
                name={name}
                Element="li"
                label={label}
                showLabel={showLabel}
                showOverrideCheckbox={showOverrideCheckbox}
                error={showError && typeof rawError === 'string' ? rawError : null}
                helpText={getSafeString(settings, 'helpText')}
                {...props}
            >
                {children}
            </BaseFormFieldContainer>
        </FormFieldContext.Provider>
    );
}
